import { useParams, Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { FormationHero } from '@/components/formations/FormationHero';
import { FormationBadges } from '@/components/formations/FormationBadges';
import { FormationInstructor } from '@/components/formations/FormationInstructor';
import { FormationObjectives } from '@/components/formations/FormationObjectives';
import { FormationProgram } from '@/components/formations/FormationProgram';
import { PracticalInfo } from '@/components/formations/PracticalInfo';
import { PedagogyEvaluation } from '@/components/formations/PedagogyEvaluation';
import { SessionsList } from '@/components/formations/SessionsList';
import { FormationFAQ } from '@/components/formations/FormationFAQ';
import { RelatedFormations } from '@/components/formations/RelatedFormations';
import { FormationCTA } from '@/components/formations/FormationCTA';
import { CourseSchema } from '@/components/SEO/CourseSchema';
import { SEOHead } from '@/components/SEO/SEOHead';
import { useFormation } from '@/hooks/useSupabase';
import { useTranslation } from 'react-i18next';

export function FormationDetailPage() {
  const { slug, locale } = useParams<{ slug: string; locale: string }>();
  const { t, i18n } = useTranslation('formationDetail');
  const currentLocale = locale || i18n.language || 'fr';
  const { data: formation, isLoading, error } = useFormation(slug || '');

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-aspexit-green border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-aspexit-gray">{t('loading')}</p>
        </div>
      </div>
    );
  }

  if (error || !formation) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md">
          <h1 className="text-4xl font-heading font-extrabold text-aspexit-black mb-6">
            {t('error.title')}
          </h1>
          <p className="text-aspexit-gray mb-6">
            {t('error.description')}
          </p>
          <a
            href={`/${currentLocale}/formations`}
            className="inline-block px-6 py-3 bg-aspexit-green text-white rounded-md font-medium hover:bg-aspexit-green-dark transition-colors"
          >
            {t('error.button')}
          </a>
        </div>
      </div>
    );
  }

  // Parse program from JSON to Module array
  const program = Array.isArray(formation.program) ? formation.program : [];

  // Get first session for next session date
  const nextSession = formation.formation_sessions?.[0];

  return (
    <div>
      <SEOHead
        title={formation.title}
        description={formation.description || formation.title}
        path={`/formations/${slug}`}
      />
      {/* Schema.org Course structured data */}
      <CourseSchema
        title={formation.title}
        description={formation.description}
        durationHours={formation.duration_hours}
        priceHT={formation.price_ht}
        priceTTC={formation.price_ttc}
        objectives={formation.objectives}
        targetAudience={formation.target_audience}
        prerequisites={formation.prerequisites}
        url={`https://aspexit.com/${currentLocale}/formations/${slug}`}
        locale={currentLocale}
        sessions={formation.formation_sessions}
      />

      {/* Breadcrumb */}
      <div className="bg-white border-b border-border py-4">
        <div className="container mx-auto px-4">
          <nav className="flex items-center gap-2 text-sm text-aspexit-gray">
            <Link to={`/${currentLocale}`} className="hover:text-aspexit-green">
              {t('breadcrumb.home')}
            </Link>
            <ChevronRight className="w-4 h-4" />
            <Link to={`/${currentLocale}/formations`} className="hover:text-aspexit-green">
              {t('breadcrumb.formations')}
            </Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-aspexit-black">{formation.title}</span>
          </nav>
        </div>
      </div>

      {/* Hero */}
      <FormationHero
        title={formation.title}
        description={formation.description}
        image={formation.image}
        price_ht={formation.price_ht}
        price_ttc={formation.price_ttc}
        category={formation.category}
      />

      {/* Main Content with pale red background */}
      <div className="bg-[#F06C6E]/8">
        {/* Badges */}
        <FormationBadges
          duration_hours={formation.duration_hours}
          min_participants={formation.min_participants}
          max_participants={formation.max_participants}
          mode={nextSession?.mode || 'distanciel'}
          nextSessionDate={nextSession?.start_date}
        />

        {/* Instructor */}
        <FormationInstructor />

        {/* Objectives */}
        <FormationObjectives objectives={formation.objectives} />

        {/* Program */}
        <FormationProgram program={program} />

        {/* Practical Info */}
        <PracticalInfo
          target_audience={formation.target_audience}
          prerequisites={formation.prerequisites}
          min_participants={formation.min_participants}
          max_participants={formation.max_participants}
          mode={nextSession?.mode || 'distanciel'}
          accessibility={formation.accessibility}
        />

        {/* Pedagogy & Evaluation */}
        <PedagogyEvaluation
          pedagogy={formation.pedagogy}
          evaluation={formation.evaluation}
        />

        {/* Sessions - Removed as per user request */}
        {/* <SessionsList
          sessions={formation.formation_sessions || []}
          price_ttc={formation.price_ttc}
          duration_hours={formation.duration_hours}
        /> */}

        {/* FAQ */}
        <FormationFAQ />

        {/* Related Formations */}
        <RelatedFormations
          currentFormationId={formation.id}
          category={formation.category}
        />

        {/* Final CTA */}
        <FormationCTA />
      </div>
    </div>
  );
}
